/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { ProductColumn } from '../../molecules/products/ProductColumn';
import { SKU } from '../../../data/mypuratos';
import { connect } from 'react-redux';
import { FlexRow } from '../../atoms/layout/FlexRow';
import { getHomepage, getLanguageIso } from '../../../selectors/generic';
import { HomepageModel } from '../../../data/aem.model';
import { addProductToCart } from '../../../actions/products';
import Block from '../../atoms/layout/Block';
import { ProductDetailsColumns } from '../../molecules/products/ProductDetailsColumns';
import {
    addToCartAction,
    isLoadingByIds,
    isLoadingByScope,
} from '../../../selectors/loader';
import { getCartItemQuantity, getCurrencyCode } from '../../../selectors/cart';
import TextColumn from '../../molecules/products/TextColumn';
import { withI18N } from '../../hoc/withI18N';
import { compose } from 'redux';
import { GTMContext } from '../../../analytics/ga4/utils/GTMContext';
import { getSkuWithNumber } from '../../../selectors/productPage';
import UnitColumn from '../../molecules/products/UnitColumn';
import { RequestPriceButton } from '../../molecules/products/RequestPriceButton';
import { RowCell } from '../../atoms/layout/RowCell';
import { selectSKUPromotion } from '../../../selectors/products';
import { googleEvents } from '../../../utils/googleEvents';
import {
    selectMyPuratosAccountEmail,
    selectUserId,
} from '../../../selectors/auth';
import { isLoggedIn } from '../../../data/user.model';
import { GA4LoginType } from '../../../analytics/ga4/types/login';
import { productEventParseAdd } from '../../../analytics/ga4/parsers/item';
import { interactionPageMapper, interactionCrossSellMapper } from '../../../analytics/ga4/utils/mapper';
import { setProductCrossSell } from '../../../actions/analytics';

class ProductRow extends React.Component {
    static defaultCellSizes = { xs: 6, sm: 3 };

    static contextType = GTMContext;

    static propTypes = {
        product: PropTypes.arrayOf(PropTypes.shape(SKU)).isRequired,
        homepage: PropTypes.shape(HomepageModel),
        addProductToCart: PropTypes.func,
        isLoading: PropTypes.bool,
        hideDetails: PropTypes.bool,
        cartQuantity: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        columns: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
        showStock: PropTypes.bool,
    };

    static defaultProps = {
        isLoading: false,
        showStock: false,
    };

    state = {
        quantity:
            Math.max(
                1,
                this.props.product.minimumOrderableQuantity -
                    (this.props.cartQuantity || 0)
            ) || 1,
        response: false,
        skuPackaging: undefined,
    };

    componentDidMount() {
        if (this.props.selected !== undefined) {
            this.setState({
                skuPackaging: getSkuWithNumber(
                    this.props.product,
                    this.props.selected
                )?.materialNumber,
            });
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            this.props.selected !== undefined &&
            prevProps.selected !== this.props.selected
        ) {
            this.setState({
                skuPackaging: getSkuWithNumber(
                    this.props.product,
                    this.props.selected
                )?.materialNumber,
            });
        }

        if (
            this.props.quantity !== undefined &&
            prevProps.quantity !== this.props.quantity
        ) {
            this.setState({ quantity: this.props.quantity });
        }
    }

    setSkuPackaging = (number) => {
        this.setState({ skuPackaging: number });
    };

    updateQuantity = (value) => {
        if (!this.props.isLoading) {
            this.setState({ quantity: value }, () => {
                if (
                    this.props.onQuantityChange &&
                    typeof this.props.onQuantityChange === 'function'
                ) {
                    this.props.onQuantityChange(value);
                }
            });
        }
    };

    addToCartCallBack = () => {
        const {
            product,
            userId,
            userEmail,
            currency,
            subTotalPrice,
            setProductCrossSell,
        } = this.props || {};

        const productPromo = product[0].promotions[0];

        const promotionData = {
            promotion_id: productPromo ? productPromo.id : '',
            promotion_name: productPromo ? productPromo.longPromoText : '',
        };

        setProductCrossSell({ ...product, type: interactionCrossSellMapper(this.context) });

        googleEvents.addToCart({
            user_id: userId,
            page_language: getLanguageIso(),
            email_address: isLoggedIn() ? userEmail : '',
            login_status: userId ? GA4LoginType.LOGGED_IN : GA4LoginType.GUEST,
            website_section: interactionPageMapper(this.context),
            subTotalPrice: subTotalPrice,
            currency: currency,
            items: productEventParseAdd(
                product,
                currency,
                interactionPageMapper(this.context),
                this.state.quantity,
                promotionData
            ),
        });
        this.setState({ quantity: 1, response: true });
    };

    dispatchAddToCart = () => {
        const { isLoading, addProductToCart, product } = this.props;
        this.setState({ response: false });
        if (!isLoading) {
            addProductToCart(
                getSkuWithNumber(product, this.state.skuPackaging),
                this.state.quantity,
                this.context,
                this.addToCartCallBack
            );
        }
    };

    renderValues = (
        packagingOptions,
        skuPackaging,
        setSkuPackaging,
        showStock
    ) => {
        const {
            homepage,
            isLoading,
            addAll,
            cartQuantity,
            hideCartAmountAlert,
            translate,
            productCategoryTag
        } = this.props;
        const {
            hasPrice = true,
            isObsolete = false,
            ...product
        } = getSkuWithNumber(packagingOptions, skuPackaging);

        if (isObsolete) {
            return (
                <>
                    {packagingOptions?.length > 1 && (
                        <UnitColumn
                            label={translate('Unit')}
                            current={product}
                            sizes={{ xxs: 12, xs: 6, sm: 4, md: 3 }}
                            options={packagingOptions}
                            setOption={setSkuPackaging}
                        />
                    )}
                    <TextColumn
                        text={homepage.obsoleteSku}
                        textSizes={{ xxs: 12, xs: 6, sm: 8, md: 9 }}
                        textCenter
                    />
                </>
            );
        } else if (!hasPrice) {
            const hasMultiplePackaging = packagingOptions?.length > 1;
            return (
                <>
                    {hasMultiplePackaging && (
                        <UnitColumn
                            label={translate('Unit')}
                            current={product}
                            sizes={{ xxs: 12, xs: 12, sm: 5, md: 3 }}
                            options={packagingOptions}
                            setOption={setSkuPackaging}
                        />
                    )}
                    <TextColumn
                        text={homepage.accompanyingButtonText}
                        textSizes={{
                            xxs: 12,
                            xs: 12,
                            sm: hasMultiplePackaging ? 7 : 12,
                            md: hasMultiplePackaging ? 5 : 8,
                        }}
                        textCenter
                    />
                    <RowCell
                        sizes={{ xxs: 12, xs: 12, sm: 12, md: 4 }}
                        className={`table-group-cell--btn`}
                    >
                        <RequestPriceButton
                            productRowData={product}
                            productCategoryTag={productCategoryTag}
                            productId={product.materialNumber}
                        />
                    </RowCell>
                </>
            );
        } else {
            const cartAmount =
                cartQuantity.filter(
                    (product) => product[0] === skuPackaging
                )[0][1] || 0;
            return (
                <ProductDetailsColumns
                    sku={product}
                    updateQuantity={this.updateQuantity}
                    quantity={this.state.quantity}
                    addToCart={this.dispatchAddToCart}
                    cartQuantity={cartAmount}
                    isLoading={isLoading}
                    addAll={addAll}
                    response={this.state.response}
                    hideCartAmountAlert={hideCartAmountAlert}
                    packagingOptions={packagingOptions}
                    setSkuPackaging={setSkuPackaging}
                    showStock={showStock}
                />
            );
        }
    };

    render() {
        const {
            product,
            hideDetails,
            skuChangeHandler,
            addEvent,
            showStock,
            position,
        } = this.props;
        const { skuPackaging } = this.state;

        if (!skuPackaging) {
            return null;
        }

        const productSku = getSkuWithNumber(product, skuPackaging);
        const promotion = selectSKUPromotion(productSku);

        return (
            <Block
                className={`ordering-modal__product-row ${
                    promotion.id ? 'promo-row' : ''
                }`}
                style={FlexRow.styles.center}
            >
                {promotion && (promotion.promoCode || promotion.id) && (
                    <p className={'promo-label'}>
                        {promotion.promoCode || promotion.id}
                    </p>
                )}
                <Block className={'row p-flex p-flex-wrap '}>
                    {!hideDetails && (
                        <Block className={'p-flex col-xs-12 col-sm-5'}>
                            <Block
                                className={
                                    'row p-flex p-flex-align-center p-flex-justify'
                                }
                            >
                                <ProductColumn
                                    product={getSkuWithNumber(
                                        product,
                                        skuPackaging
                                    )}
                                    addEvent={addEvent}
                                    position={position}
                                    showStock={showStock}
                                />
                            </Block>
                        </Block>
                    )}
                    <Block
                        className={
                            'p-flex p-flex-align-center col-xs-12' +
                            (!hideDetails ? ' col-sm-7' : '')
                        }
                    >
                        <Block
                            className={`row p-flex p-flex-wrap p-flex-justify p-flex-justify-content-end`}
                        >
                            {this.renderValues(
                                product,
                                skuPackaging,
                                skuChangeHandler
                                    ? skuChangeHandler
                                    : this.setSkuPackaging,
                                showStock
                            )}
                        </Block>
                    </Block>
                </Block>
            </Block>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    homepage: getHomepage(state),
    cartQuantity: ownProps.product.map((product) => [
        product.materialNumber,
        getCartItemQuantity(state, product.materialNumber),
    ]),
    isLoading: !ownProps.addAll
        ? isLoadingByIds(
              state,
              ownProps.product.map((item) =>
                  addToCartAction(item.materialNumber)
              )
          )
        : isLoadingByScope(state, addToCartAction()),
    userId: selectUserId(state),
    currency: getCurrencyCode(state),
    userEmail: selectMyPuratosAccountEmail(state),
    subTotalPrice: state?.myPuratos_cart?.data?.subTotalDouble,
});

const mapActionsToProps = {
    addProductToCart,
    setProductCrossSell,
};

export default compose(
    connect(mapStateToProps, mapActionsToProps),
    withI18N
)(ProductRow);
